import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle2, XCircle, ArrowLeft, Loader2 } from 'lucide-react';
import styles from '../css/manualCheckout.module.css';
import { updateTicketStatus } from '../helpers/databaseAPI';

/**
 * CloseTicketScreen
 * A focused, single-purpose screen for an employee to close a support ticket.
 * Fields: Card Number, Ticket Number.
 * Actions: Cancel (return home), Submit (close ticket).
 */
export default function CloseTicketScreen({ onCancel, onSubmit }) {
  const [cardNumber, setCardNumber] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success
  const cardNumRef = useRef(null);
  const ticketNumRef = useRef(null);
  const cancelBtnRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    cardNumRef.current.focus();
  }, []);

  const validate = () => {
    const next = {};
    if (!cardNumber.trim()) next.cardNumber = 'Enter a card number.';
    // The comma in regex means that the entry must be greater than zero and less than
    // or equal to 7. If I add 7, them the entry must be at least 7.
    else if (!/^\d{1,7}$/.test(cardNumber.trim())) next.cardNumber = 'Card number should be 1 to 7 digits.';

    if (!ticketNumber.trim()) next.ticketNumber = 'Enter a ticket number.';
    else if (!/^\d{7}$/.test(ticketNumber.trim())) next.ticketNumber = 'Ticket number can only be 7 digits.';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('submitting');
    // Simulate an async close-ticket call. Replace with real submit handler.
    setTimeout(() => {
      setStatus('success');
      if (onSubmit) onSubmit({ cardNumber: cardNumber.trim(), ticketNumber: ticketNumber.trim() });
    }, 900);
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  if (status === 'success') {
    return (
      <div className={styles.page}>
        <div className={styles.confirmCard}>
          <CheckCircle2 size={40} color="#1E7A4C" strokeWidth={1.75} />
          <h2 className={styles.confirmTitle}>Ticket closed</h2>
          <p className={styles.confirmBody}>
            Ticket <span className={styles.mono}>{ticketNumber}</span> has been closed and logged.
          </p>
          <button className={styles.primaryBtn} onClick={handleCancel}>
            Return home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerRule} />
          <h1 className={styles.title}>Close Ticket</h1>
          <p className={styles.subtitle}>Confirm the card and ticket number to close this request.</p>
        </header>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <label htmlFor="cardNumber" className={styles.label}>
              Card number
            </label>
            <input
              ref={cardNumRef}
              id="cardNumber"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="e.g. Card 1234"
              value={cardNumber}
              onChange={(e) => {
                setCardNumber(e.target.value);
                if (errors.cardNumber) setErrors((p) => ({ ...p, cardNumber: undefined }));
              }}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  ticketNumRef.current.focus();
                }
              }}
              className={{
                ...styles.input,
                ...(errors.cardNumber ? styles.inputError : {}),
              }}
              aria-invalid={!!errors.cardNumber}
              aria-describedby={errors.cardNumber ? 'cardNumber-error' : undefined}
            />
            {errors.cardNumber && (
              <span id="cardNumber-error" className={styles.errorText}>
                {errors.cardNumber}
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="ticketNumber" className={styles.label}>
              Ticket number
            </label>
            <input
              id="ticketNumber"
              ref={ticketNumRef}
              type="text"
              autoComplete="off"
              placeholder="e.g. Ticket-1234567"
              value={ticketNumber}
              onChange={(e) => {
                setTicketNumber(e.target.value);
                if (errors.ticketNumber) setErrors((p) => ({ ...p, ticketNumber: undefined }));
              }}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  closeBtnRef.current?.focus();
                }
              }}
              className={{
                ...styles.input,
                ...(errors.ticketNumber ? styles.inputError : {}),
              }}
              aria-invalid={!!errors.ticketNumber}
              aria-describedby={errors.ticketNumber ? 'ticketNumber-error' : undefined}
            />
            {errors.ticketNumber && (
              <span id="ticketNumber-error" className={styles.errorText}>
                {errors.ticketNumber}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className={styles.actions}>
            <button
              ref={cancelBtnRef}
              type="button"
              onClick={handleCancel}
              className={styles.secondaryBtn}
              disabled={status === 'submitting'}
            >
              <ArrowLeft size={16} strokeWidth={2} />
              Cancel
            </button>
            <button
              type="submit"
              ref={closeBtnRef}
              // className={{
              //   ...styles.primaryBtn,
              //   ...(status === 'submitting' ? styles.primaryBtnDisabled : {}),
              // }}
              className={styles.primaryBtn}
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 size={16} className="spin" strokeWidth={2} />
                  Closing…
                </>
              ) : (
                <>
                  <XCircle size={16} strokeWidth={2} />
                  Close ticket
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }
        .spin { animation: spin 0.8s linear infinite; }
        input::placeholder { color: #A3A9B7; }
      `}</style>
    </div>
  );
}

// const PALETTE = {
//   bg: '#EEF1F6',
//   card: '#FFFFFF',
//   ink: '#1B2333',
//   inkSoft: '#5B6478',
//   line: '#DDE2EC',
//   navy: '#1F3A5F',
//   navyDeep: '#16283F',
//   accent: '#C2542F',
//   danger: '#B3261E',
//   successBg: '#F1FBF5',
// };

// const styles = {
//   page: {
//     minHeight: '100vh',
//     width: '100%',
//     background: PALETTE.bg,
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     padding: '32px 16px',
//     fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
//     boxSizing: 'border-box',
//   },
//   card: {
//     width: '100%',
//     maxWidth: 440,
//     background: PALETTE.card,
//     borderRadius: 10,
//     border: `1px solid ${PALETTE.line}`,
//     boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(16,24,40,0.06)',
//     padding: '0 32px 32px',
//     overflow: 'hidden',
//   },
//   header: {
//     padding: '28px 0 20px',
//     marginBottom: 8,
//   },
//   headerRule: {
//     width: 40,
//     height: 4,
//     borderRadius: 2,
//     background: PALETTE.accent,
//     marginBottom: 16,
//   },
//   title: {
//     margin: 0,
//     fontSize: 22,
//     fontWeight: 700,
//     letterSpacing: '-0.01em',
//     color: PALETTE.ink,
//   },
//   subtitle: {
//     margin: '6px 0 0',
//     fontSize: 13.5,
//     color: PALETTE.inkSoft,
//     lineHeight: 1.5,
//   },
//   field: {
//     marginBottom: 18,
//     display: 'flex',
//     flexDirection: 'column',
//   },
//   label: {
//     fontSize: 12.5,
//     fontWeight: 600,
//     color: PALETTE.ink,
//     marginBottom: 6,
//     letterSpacing: '0.01em',
//   },
//   input: {
//     height: 44,
//     borderRadius: 7,
//     border: `1px solid ${PALETTE.line}`,
//     padding: '0 14px',
//     fontSize: 14.5,
//     color: PALETTE.ink,
//     background: '#FBFCFE',
//     outline: 'none',
//     fontFamily: "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace",
//     letterSpacing: '0.02em',
//     transition: 'border-color 120ms ease, box-shadow 120ms ease',
//     boxSizing: 'border-box',
//   },
//   inputError: {
//     borderColor: PALETTE.danger,
//     background: '#FDF6F5',
//   },
//   errorText: {
//     marginTop: 6,
//     fontSize: 12.5,
//     color: PALETTE.danger,
//   },
//   actions: {
//     display: 'flex',
//     gap: 10,
//     marginTop: 8,
//   },
//   secondaryBtn: {
//     flex: 1,
//     height: 44,
//     borderRadius: 7,
//     border: `1px solid ${PALETTE.line}`,
//     background: '#FFFFFF',
//     color: PALETTE.inkSoft,
//     fontSize: 14,
//     fontWeight: 600,
//     display: 'inline-flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     gap: 6,
//     cursor: 'pointer',
//   },
//   primaryBtn: {
//     flex: 1,
//     height: 44,
//     borderRadius: 7,
//     border: 'none',
//     background: PALETTE.navy,
//     color: '#FFFFFF',
//     fontSize: 14,
//     fontWeight: 600,
//     display: 'inline-flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     gap: 6,
//     cursor: 'pointer',
//     boxShadow: '0 1px 2px rgba(22,40,63,0.15)',
//   },
//   primaryBtnDisabled: {
//     background: PALETTE.navyDeep,
//     opacity: 0.75,
//     cursor: 'not-allowed',
//   },
//   confirmCard: {
//     width: '100%',
//     maxWidth: 400,
//     background: PALETTE.successBg,
//     borderRadius: 10,
//     border: '1px solid #CFE9DA',
//     padding: '40px 32px',
//     display: 'flex',
//     flexDirection: 'column',
//     alignItems: 'center',
//     textAlign: 'center',
//     fontFamily: "'Inter', sans-serif",
//   },
//   confirmTitle: {
//     margin: '14px 0 6px',
//     fontSize: 19,
//     fontWeight: 700,
//     color: '#14532D',
//   },
//   confirmBody: {
//     margin: '0 0 20px',
//     fontSize: 13.5,
//     color: '#3A5A47',
//   },
//   mono: {
//     fontFamily: "'JetBrains Mono', monospace",
//     fontWeight: 600,
//   },
// };
